import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ProfileSelectorOption from 'app/components/profileSelector/components/ProfileSelectorOption';
import ProfileSelectorInfoMessage from 'app/components/profileSelector/components/ProfileSelectorInfoMessage';
import PageTypeUtils from 'app/utils/pageType';
import { PageNumberType } from '@kontentino/kontentino-constants/Pages';
import { ActionButton } from 'app/components/actions/ActionButton';
import { useActions } from 'app/hooks/useActions';
import type { PageData, ProfileGroup } from 'types/Page';
import useAnalyticsProfileOptionActions from 'app/modules/analytics/components/analyticsProfileSelector/hooks/useAnalyticsProfileOptionActions';

interface AnalyticsProfileSelectorOptionsProps {
  analyticsPages: PageData[];
  filteredProfiles: PageData[];
  filteredProfileGroups: ProfileGroup[];
  selectedPageId: number | null;
  selectedGroupId: number | null;
  onSelectPage: (page: PageData) => void;
  onSelectGroup: (groupId: number) => void;
}

const AnalyticsProfileSelectorOptions: FC<
  AnalyticsProfileSelectorOptionsProps
> = ({
  analyticsPages,
  filteredProfiles,
  filteredProfileGroups,
  selectedPageId,
  selectedGroupId,
  onSelectPage,
  onSelectGroup,
}) => {
  const { t } = useTranslation();

  const supportedAnalyticsPageIds = useMemo(
    () => new Set(analyticsPages.map((p) => p.id)),
    [analyticsPages],
  );

  const unsupportedProfileTooltip = t(
    'analyticsPerformance.profileNotSupportedInAnalytics',
  );
  const { profileActions } = useActions('profile-selector');
  const {
    handleEditProfileGroupClick,
    handleEditProfileClick,
    canEditProfile,
    canEditProfileGroup,
    handleConnectionClick,
  } = useAnalyticsProfileOptionActions();

  const isEmpty =
    filteredProfileGroups.length === 0 && filteredProfiles.length === 0;

  if (isEmpty) {
    return (
      <ProfileSelectorInfoMessage
        message={t('profileSelector.emptyMessage')}
        ctaButton={
          <ActionButton
            action={profileActions.add}
            data-name="analytics-profile-select-empty-message"
            data-cy="analytics-profile-select-empty-message"
            variant="plain"
            className="tw-text-primary-100"
          />
        }
      />
    );
  }

  return (
    <>
      {filteredProfileGroups.map((group) => (
        <ProfileSelectorOption
          key={`group-option-${group.id}`}
          isSingleSelect
          dataName={`analytics-profile-select-group-${group.id}`}
          dataCy="analytics_profile-selector_option"
          option={{
            label: group.name,
            logoSrc: group.logo?.src ?? undefined,
            isGroup: true,
            isConnected: !!group.isConnected,
          }}
          subOptions={group.pages.map((page) => {
            const isSupported = supportedAnalyticsPageIds.has(page.id);
            return {
              id: page.id,
              label: page.name,
              logoSrc: page.logo?.src ?? undefined,
              socialMediaType: page.type,
              isConnected: !!page.isConnected,
              isGlobal: page.isGlobal,
              isDisabled: !isSupported,
              disabledTooltip: !isSupported
                ? unsupportedProfileTooltip
                : undefined,
            };
          })}
          eventHandlers={{
            onClick: () => onSelectGroup(group.id),
            onEditClick: canEditProfileGroup(group.isEditable)
              ? () => handleEditProfileGroupClick(group.id)
              : undefined,
            onSubOptionReconnectClick: canEditProfileGroup(group.isEditable)
              ? (page) => handleConnectionClick(page)
              : undefined,
          }}
          isChecked={selectedGroupId === group.id}
        />
      ))}
      {filteredProfiles.map((page) => (
        <ProfileSelectorOption
          isSingleSelect
          dataName={`analytics-profile-select-${PageTypeUtils.getName(
            page.type as PageNumberType,
          )}`}
          key={`page-option-${page.id}`}
          dataCy="analytics_profile-selector_option"
          option={{
            label: page.name ?? '',
            socialMediaType: page.type,
            logoSrc: page.logo?.src ?? undefined,
            isConnected: !!page.isConnected,
            isGlobal: page.isGlobal,
          }}
          eventHandlers={{
            onClick: () => onSelectPage(page),
            onEditClick: canEditProfile(page.isDemo)
              ? () => handleEditProfileClick(page.id, page.isGlobal)
              : undefined,
            onReconnectClick:
              page.isConnected || !canEditProfile(page.isDemo)
                ? undefined
                : () =>
                    handleConnectionClick({
                      id: page.id,
                      type: page.type,
                    }),
          }}
          isChecked={selectedGroupId === null && selectedPageId === page.id}
          isDemo={page.isDemo}
          isGlobal={page.isGlobal}
        />
      ))}
    </>
  );
};

export default AnalyticsProfileSelectorOptions;
